/* eslint-disable no-param-reassign */
import { createReducer } from '@reduxjs/toolkit';
import {
  addSignals,
  loadingSignalShares,
  removeShare,
  reset,
  setSelectedSignal,
  setSignals,
  setSignalShares,
  updateSignalById,
} from './actions';
import { Signal, SignalShare } from './models';

interface StoredSignals {
  [key: string]: Signal;
}

export interface SignalState {
  signals: StoredSignals;
  selectedSignal: string | undefined;
  signalSharesLoading: boolean;
  signalShares: {
    [id: string]: SignalShare;
  };
}

const initialState: SignalState = {
  signals: {},
  signalSharesLoading: false,
  selectedSignal: undefined,
  signalShares: {},
};

// eslint-disable-next-line import/prefer-default-export
export const reducer = createReducer(initialState, (builder) => {
  builder.addCase(updateSignalById, (state, action) => {
    const incoming = action.payload as Signal;
    const existing = state.signals[incoming.id];
    // Honour explicit null (anonymous/boat) from the server so the avatar is
    // cleared. Only fall back to the cached image when the field is absent.
    const image: string | null =
      incoming.image !== undefined ? incoming.image : (existing?.image ?? null);
    state.signals[incoming.id] = { ...incoming, image };
  });

  builder.addCase(loadingSignalShares, (state, action) => {
    state.signalSharesLoading = action.payload;
  });

  builder.addCase(addSignals, (state, action) => {
    const payload = action.payload as StoredSignals;
    const merged: StoredSignals = { ...state.signals };
    Object.entries(payload).forEach(([id, incoming]) => {
      const existing = merged[id];
      const incomingImage = incoming?.image;
      const existingImage = existing?.image;
      // Honour explicit null (anonymous/boat) so the avatar is cleared.
      // Only fall back to the cached image when the field is absent.
      const image: string | null =
        incomingImage !== undefined ? incomingImage : (existingImage ?? null);
      merged[id] = { ...incoming, image };
    });
    state.signals = merged;
  });

  builder.addCase(setSignals, (state, action) => {
    state.signals = action.payload;
  });

  builder.addCase(reset, (state) => {
    Object.assign(state, initialState);
  });

  builder.addCase(setSelectedSignal, (state, action) => {
    state.selectedSignal = action.payload;
  });

  builder.addCase(setSignalShares, (state, action) => {
    state.signalShares = {
      ...state.signalShares,
      ...action.payload,
    };
  });

  builder.addCase(removeShare, (state, action) => {
    delete state.signalShares[action.payload];
  });
});
